<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { BaseButton, BaseDrawer, BaseHoverLabel, BaseIcon } from "@lunagoai/vue-ui";

export type AdminShellNavItem = { id: string; label: string; icon: string; active: boolean };
export type AdminShellNavGroup = { label: string; items: AdminShellNavItem[] };

const emit = defineEmits<{
  (event: "navigate", id: string): void;
  (event: "refresh"): void;
}>();

const drawerOpen = ref(false);
const isCompact = ref(false);
let compactMedia: MediaQueryList | undefined;
const props = defineProps<{
  navGroups: AdminShellNavGroup[];
  refreshLabel: string;
  refreshing?: boolean;
  refreshDisabled?: boolean;
  drawerTheme?: "light" | "dark";
}>();

function navigate(id: string): void {
  drawerOpen.value = false;
  emit("navigate", id);
}

function updateCompactMode(): void {
  isCompact.value = Boolean(compactMedia?.matches);
}

onMounted(() => {
  compactMedia = window.matchMedia("(max-width: 1100px)");
  updateCompactMode();
  compactMedia.addEventListener("change", updateCompactMode);
});

onBeforeUnmount(() => compactMedia?.removeEventListener("change", updateCompactMode));
</script>

<template>
  <div class="min-h-screen bg-[var(--pn-bg)] text-[var(--pn-text)]">
    <header class="sticky top-0 z-20 border-b border-[var(--pn-border)] bg-[color-mix(in_srgb,var(--pn-surface)_92%,transparent)] backdrop-blur">
      <div class="mx-auto flex min-h-14 max-w-[1480px] items-center justify-between gap-4 px-6 py-2 max-[1100px]:min-h-[3.25rem] max-[1100px]:px-4 max-[1100px]:py-[.4rem] max-[760px]:gap-2">
        <div class="flex min-w-0 items-center gap-2">
          <BaseButton v-if="isCompact" class="text-[var(--pn-text-muted)] hover:bg-[var(--pn-surface-muted)]" variant="transparent" size="sm" icon="List" :aria-label="props.navGroups[0]?.label || 'Menu'" @click="drawerOpen = true" />
          <slot name="brand" />
        </div>
        <div class="ml-auto flex min-w-0 items-center gap-3">
          <slot name="header-context" />
          <div class="shrink-0"><slot name="header-actions" /></div>
        </div>
      </div>
    </header>

    <main class="mx-auto grid max-w-[1480px] grid-cols-[minmax(14rem,15rem)_minmax(0,1fr)] gap-6 px-6 py-8 pb-12 max-[1100px]:block max-[1100px]:px-4 max-[1100px]:py-4 max-[1100px]:pb-8">
      <aside v-if="!isCompact" class="sticky top-20 self-start rounded-2xl border border-[var(--pn-border)] bg-[var(--pn-surface)] p-[.6rem] text-[var(--pn-text)] shadow-xl">
        <nav class="grid gap-[.8rem] pt-[.65rem] max-[1100px]:grid-cols-2 max-[1100px]:gap-x-4 max-[1100px]:gap-y-3" aria-label="Admin navigation">
          <div v-for="group in navGroups" :key="group.label">
            <div class="px-2 pb-1 text-[.5625rem] font-extrabold uppercase tracking-[.13em] text-[var(--pn-text-subtle)]">{{ group.label }}</div>
            <div class="space-y-1">
              <BaseButton v-for="item in group.items" :key="item.id" class="group w-full min-h-[2.2rem] justify-start gap-2 whitespace-normal px-2 py-[.45rem] text-xs" align="start" variant="transparent" :class="item.active ? 'bg-[var(--pn-selection-bg)] text-[var(--pn-selection-text)] font-bold shadow-[inset_0_0_0_1px_var(--pn-selection-ring)] hover:bg-[var(--pn-selection-bg)] hover:text-[var(--pn-selection-text)] hover:shadow-[inset_0_0_0_2px_var(--pn-selection-ring)]' : 'text-[var(--pn-text-muted)] hover:bg-[var(--pn-surface-muted)] hover:text-[var(--pn-text)]'" @click="navigate(item.id)">
                <span class="grid size-[1.35rem] shrink-0 place-items-center rounded-[.4rem]" :class="item.active ? 'bg-[var(--pn-selection-bg)] text-[var(--pn-selection-text)]' : 'bg-[var(--pn-surface-muted)] text-[var(--pn-text-subtle)] group-hover:bg-[color-mix(in_srgb,var(--pn-brand)_12%,var(--pn-surface-muted))] group-hover:text-[var(--pn-text)]'"><BaseIcon :name="item.icon" :size="16" decorative /></span><span class="min-w-0 break-words text-left leading-tight">{{ item.label }}</span>
              </BaseButton>
            </div>
          </div>
        </nav>
      </aside>

      <BaseDrawer v-model:visible="drawerOpen" position="left" :theme="props.drawerTheme ?? 'dark'" width="min(18rem, 88vw)" :title="props.navGroups[0]?.label || 'Menu'" :aria-label="props.navGroups[0]?.label || 'Menu'">
        <nav class="grid gap-4 pt-2" aria-label="Admin navigation">
          <div v-for="group in props.navGroups" :key="group.label">
            <div class="px-2 pb-1 text-[.5625rem] font-extrabold uppercase tracking-[.13em] text-[var(--pn-text-subtle)]">{{ group.label }}</div>
            <div class="space-y-1">
              <BaseButton v-for="item in group.items" :key="item.id" class="group w-full min-h-[2.35rem] justify-start gap-2 whitespace-normal px-2 py-[.5rem] text-sm" align="start" variant="transparent" :class="item.active ? 'bg-[var(--pn-selection-bg)] text-[var(--pn-selection-text)] font-bold shadow-[inset_0_0_0_1px_var(--pn-selection-ring)]' : 'text-[var(--pn-text-muted)] hover:bg-[var(--pn-surface-muted)] hover:text-[var(--pn-text)]'" @click="navigate(item.id)">
                <span class="grid size-[1.4rem] shrink-0 place-items-center rounded-[.4rem]" :class="item.active ? 'bg-[var(--pn-selection-bg)] text-[var(--pn-selection-text)]' : 'bg-[var(--pn-surface-muted)] text-[var(--pn-text-subtle)]'"><BaseIcon :name="item.icon" :size="16" decorative /></span><span class="min-w-0 break-words text-left leading-tight">{{ item.label }}</span>
              </BaseButton>
            </div>
          </div>
        </nav>
      </BaseDrawer>

      <section class="min-w-0">
        <div class="mb-4 flex items-center justify-between gap-6">
          <nav class="inline-flex items-center gap-1.5 text-xs text-[var(--pn-text-muted)]" aria-label="Breadcrumb"><slot name="breadcrumb" /></nav>
          <BaseHoverLabel :text="refreshLabel" placement="bottom"><template #trigger><BaseButton variant="secondary" size="sm" :loading="refreshing" :loading-label="refreshLabel" :disabled="refreshDisabled" :aria-label="refreshLabel" @click="emit('refresh')"><BaseIcon v-if="!refreshing" name="RotateCw" :size="14" decorative /><span v-if="!refreshing">{{ refreshLabel }}</span></BaseButton></template></BaseHoverLabel>
        </div>
        <slot />
      </section>
    </main>
  </div>
</template>
